import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * The AI transparency note (`docs/landing/ai-transparency.md`), published for the first time.
 *
 * Counsel-cleared 2026-08-08 and never routed ([B-271](../../../../docs/platform/TODO.md)).
 *
 * ⚠️ **Section 3's column split is the point of the page and must survive transcription.** Column
 * two is what *we* control and guarantee; column three is the provider's own posture, **theirs to
 * state and change**. Collapsing the two — the natural thing to do when a table gets narrow — would
 * turn a vendor's current terms into a Dedupr guarantee, which is the misrepresentation the
 * document's own preamble exists to prevent. Both the caveat and the per-column framing are kept.
 *
 * ⚠️ **Section 7 ("what is not built") is kept in full.** A transparency note that lists only what
 * exists is marketing.
 */
export interface AiTransparencyProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

const SENT: { op: string; leaves: React.ReactNode; not: React.ReactNode }[] = [
  {
    op: 'Duplicate adjudication',
    leaves: <>The identity-relevant fields of exactly <strong>two</strong> records — typically name, email, phone, company, and the normalised forms used for comparison</>,
    not: <>Record ids you would recognise, other records in the cluster, notes and description fields, attachments, anything from a different object</>,
  },
  {
    op: 'Document classification',
    leaves: <>A document&rsquo;s <strong>name, path fragment and a bounded content excerpt</strong> where content extraction is enabled</>,
    not: <>The full document, unless you have enabled full-content processing; permissions; anything from a library you have not opted into</>,
  },
  {
    op: 'Embeddings',
    leaves: <>A templated projection of a <strong>golden record&rsquo;s</strong> fields, and — <strong>only if you opt in per field</strong> — long free-text fields</>,
    not: <>Anything from an undecided or speculative merge; any field your masking policy excludes</>,
  },
  {
    op: 'Normalisation suggestions',
    leaves: <>The distinct <strong>values</strong> of one field (e.g. the 40 spellings of a job title), not the records carrying them</>,
    not: <>The records themselves</>,
  },
];

const PROVIDERS: { name: string; ours: React.ReactNode; theirs: React.ReactNode; status: React.ReactNode }[] = [
  {
    name: 'Anthropic',
    ours: <>API only, no fine-tuning, no persistent conversation state. Keys held per organisation under envelope encryption, never in a browser, never logged. Available with <strong>your own key</strong></>,
    theirs: <>Commercial API inputs and outputs are <strong>not used to train models</strong>; retained for a limited abuse-monitoring period. Zero-retention arrangements exist for qualifying commercial customers</>,
    status: <>Built and callable</>,
  },
  {
    name: 'OpenAI',
    ours: <>API only, no fine-tuning, no persistent state. Same key custody. Available with <strong>your own key</strong></>,
    theirs: <>Business/API data is <strong>not used to train models</strong> by default; a limited abuse-monitoring retention applies, with zero-retention available on qualifying arrangements</>,
    status: <>Built and callable</>,
  },
  {
    name: 'Cloudflare Workers AI',
    ours: <>The <strong>keyless default</strong> — runs on the same infrastructure the rest of the platform runs on, so no additional vendor is introduced and no key exists to leak</>,
    theirs: <>Inference on Cloudflare&rsquo;s own network under its terms</>,
    status: <>Built and callable; the default route for classification, extraction and embeddings</>,
  },
  {
    name: 'Azure OpenAI',
    ours: <>Declared and <strong>refuses by name</strong> (<code>provider_not_built</code>)</>,
    theirs: <>Would be the answer for a customer requiring an EU-resident model endpoint under their own Azure agreement</>,
    status: <><strong>Not built.</strong> It refuses rather than silently routing elsewhere, because a provider that pretended would be worse than a refusal</>,
  },
  {
    name: 'Google Gemini',
    ours: <>Declared and <strong>refuses by name</strong></>,
    theirs: <>—</>,
    status: <><strong>Not built</strong>, same treatment</>,
  },
];

export function AiTransparency(props: AiTransparencyProps) {
  return (
    <PolicyPage
      section="Trust"
      title="AI transparency note"
      intro="What is sent to a model, to whom, what they may do with it, and what we can prove afterwards."
      reviewed="2026-08-08"
      counselCleared
      {...props}
    >
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>Why this is separate from the DPA.</strong> &ldquo;We use AI responsibly&rdquo; is
          not an answer an AI-governance reviewer can act on. This states, per provider, what leaves
          your region, what the provider is contractually able to do with it, and what we can prove
          afterwards — and marks clearly which of those facts are ours to guarantee and which are the
          provider&rsquo;s to state.
        </div>
      </div>

      <h2>The three sentences that bound everything below</h2>
      <ol>
        <li>
          <strong>A model never executes anything.</strong> Its output is a proposal — a verdict on a
          duplicate pair, a document&rsquo;s likely type, a suggested normalisation.{' '}
          <strong>A person approves every change that reaches your systems</strong>, without exception
          and with no configuration that removes the person.
        </li>
        <li>
          <strong>A model sees the match-relevant fields of one pair.</strong> Not a full record, not
          related records, not free-text notes unless you have explicitly configured a field as match
          evidence. Values are length-capped and passed as delimited <strong>data</strong>, never
          interpolated into the instruction text.
        </li>
        <li>
          <strong>You can use your own keys</strong>, in which case evidence goes only to your own
          approved vendor under your own agreement with them, and <strong>Dedupr is not an AI
          sub-processor for you at all</strong>.
        </li>
      </ol>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          Because output is advisory and human-approved, the worst case of a bad model answer —
          including one caused by a prompt-injection attempt embedded in one of your own CRM fields —
          is a <strong>wrong proposal a reviewer rejects</strong>. The adjudicator has no tools and no
          actions available to it. Our legal position is that this places the use outside high-risk
          classification; that determination is <strong>about the current system</strong>, and any
          future capability that let a model&rsquo;s output take effect without a person would be a
          new question rather than an extension of it.
        </div>
      </div>

      <h2>What is actually sent</h2>
      <dl className="land-legal__qa">
        {SENT.map((row) => (
          <div key={row.op}>
            <dt>{row.op}</dt>
            <dd>
              <p><strong>What leaves:</strong> {row.leaves}</p>
              <p><strong>What does not:</strong> {row.not}</p>
            </dd>
          </div>
        ))}
      </dl>
      <p>
        <strong>Batching.</strong> Adjudication may batch a small number of pairs per call for cost.
        Two controls make that safe rather than assumed safe: a response that cannot be{' '}
        <strong>decomposed into a per-pair verdict</strong> is treated as a failed call rather than a
        partial success, and a rolling sample is <strong>re-adjudicated one pair per call</strong> and
        compared against the batched verdict — the measurement that would detect one pair&rsquo;s
        content influencing another&rsquo;s answer.
      </p>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>One honest limit:</strong> verdicts, rationales and tier agreement are measurable
          per pair, but <strong>cost is an allocation, not a measurement</strong> — tokens bill per
          call. Receipts label it as an allocation rather than presenting it as a measured per-pair
          figure.
        </div>
      </div>

      <h2>Per provider</h2>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>Read &ldquo;our configuration&rdquo; as ours and &ldquo;provider posture&rdquo; as
          theirs.</strong> The first is what Dedupr controls and guarantees. The second describes the
          provider&rsquo;s own posture, which is <strong>theirs to state and change</strong> — we set
          our configuration to the retention- and training-minimising option each offers, and{' '}
          <strong>the provider&rsquo;s current terms are the authority, not this page</strong>.
        </div>
      </div>
      <dl className="land-legal__qa">
        {PROVIDERS.map((p) => (
          <div key={p.name}>
            <dt>{p.name}</dt>
            <dd>
              <p><strong>Our configuration:</strong> {p.ours}</p>
              <p><strong>Provider posture (verify at their source):</strong> {p.theirs}</p>
              <p><strong>Status:</strong> {p.status}</p>
            </dd>
          </div>
        ))}
      </dl>
      <p>
        <strong>No provider has been called with a real key in a customer context</strong>, because
        Dedupr is pre-launch. The gateway, the key custody and the refusals are built and tested; the
        vendor relationships are not yet exercised.
      </p>

      <h2>Residency</h2>
      <ul>
        <li><strong>In-region by default.</strong> An EU-pinned organisation&rsquo;s model calls go only to in-region endpoints.</li>
        <li><strong>Cross-region is opt-in only</strong>, by one of your Admins, and the consent is audited.</li>
        <li>Without an opt-in and with no in-region model available, the AI step <strong>does not quietly leave the region — it degrades</strong> to deterministic rules and human review. A pair that would have been adjudicated is simply reviewed by a person.</li>
        <li>This is the platform&rsquo;s <strong>only</strong> opt-in that moves content across a region boundary, and it is narrow by construction: the fields of one pair, under an explicit consent, with minimisation still applying.</li>
      </ul>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>One recorded exception, ours to own rather than yours to discover.</strong>{' '}
          Embeddings published to <strong>Cloudflare Vectorize</strong> cannot currently be shown to
          be region-pinned. Rather than claim otherwise, we publish for every region and write the
          exposure to your own audit trail with the posture recorded at the moment of publication, so
          an audit can see exactly what happened. This is a recorded exception to our residency rule,
          not a change to it.
        </div>
      </div>

      <h2>What we can prove afterwards</h2>
      <p>
        Every AI-influenced decision — and every decision that mutates one of your systems, model
        involved or not — produces an immutable <strong>receipt</strong>:
      </p>
      <ul>
        <li><code>prompt_id@version</code> (prompts are immutably versioned, so a past decision is reproducible)</li>
        <li>provider, model, and the <strong>region the call ran in</strong></li>
        <li>token counts and cost</li>
        <li>the verdict and a reference to its structured evidence</li>
        <li><strong>the deterministic tier that would have applied without the model</strong> — which is what lets you ask &ldquo;did the AI actually change any outcome?&rdquo; and get an answer</li>
        <li>the person who approved the resulting action, and that action&rsquo;s outcome</li>
      </ul>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>A receipt holds field names and salted hashes, never field values.</strong> That is
          a data-minimisation requirement first, and it has a second effect worth knowing: an erasure
          request can never destroy your compliance evidence, because there is no personal data in it
          to erase. And <strong>no receipt, no decision acted on</strong> — the recording function
          returns only the verdicts whose receipt was written, so a caller cannot act on an
          unreceipted one by forgetting to check something.
        </div>
      </div>
      <p>
        <strong>Structured evidence, never prose, leaves the review screen.</strong> Which signals
        matched and at what confidence are published to surfaces and agents; the model&rsquo;s
        free-text rationale is <strong>not</strong>, because prose quotes field values and would
        become a route around the field-level masking policy. Reviewers see the prose in the app.
      </p>

      <h2>Cost and control</h2>
      <ul>
        <li><strong>Your own keys carry no markup.</strong> Dedupr-provided keys are billed at provider cost plus a stated margin, computed from a dated, editable rate table with the formula shown on every line — not an opaque figure.</li>
        <li><strong>Per-organisation monthly and per-job cost caps.</strong> Your plan sets a default; you may raise or lower it, and there is no ceiling we impose on a willing payer. Raising it is an audited act.</li>
        <li><strong>Hitting a cap parks work; it does not throw.</strong> In-flight calls finish, dispatch stops at the next checkpoint, remaining ambiguous pairs move to human review, the job completes, and your Admins are alerted. A cap is a value, not a failure.</li>
        <li><strong>You can turn AI off entirely.</strong> A fully deterministic run is a supported configuration, not a degraded one.</li>
      </ul>

      <h2>What is not built</h2>
      <dl className="land-legal__qa">
        <div>
          <dt>The prompt library itself</dt>
          <dd>The gateway routes prompts and versions them; the actual prompt content and its golden test sets are <strong>not written</strong>. Nothing here should be read as describing tested prompt behaviour.</dd>
        </div>
        <div>
          <dt>Azure OpenAI and Gemini</dt>
          <dd>Declared and refusing by name.</dd>
        </div>
        <div>
          <dt>A live provider call under a customer&rsquo;s key</dt>
          <dd>Has never happened — pre-launch.</dd>
        </div>
      </dl>
    </PolicyPage>
  );
}
